// ============================================
// Main Layout Component with i18n
// ============================================

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  Store,
  FileText,
  Users,
  BarChart3
} from 'lucide-react';
import { useAuthStore, useUIStore } from '../stores';
import { cn, projectLabels } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

export function Layout() {
  const navigate = useNavigate();
  const { t, isRTL } = useTranslation();
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar, activeProject, setActiveProject } = useUIStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/crm', icon: BarChart3, label: 'لوحة تحكم CRM' },
    { to: '/command-center', icon: LayoutDashboard, label: t('nav.commandCenter') },
    { to: '/whatsapp', icon: MessageSquare, label: t('nav.whatsapp') },
    { to: '/reports', icon: FileText, label: t('nav.reports') },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 z-30 w-64 bg-slate-900 border-slate-800',
          isRTL ? 'right-0 border-l' : 'left-0 border-r',
          'transform transition-transform duration-200 ease-in-out',
          'lg:translate-x-0 lg:static',
          sidebarOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full')
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">{t('app.title')}</h1>
              <p className="text-2xs text-slate-500">{t('nav.commandCenter')}</p>
            </div>
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden btn-icon"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Project Filter */}
        <div className="p-4 border-b border-slate-800">
          <label className="label">{t('projects.label')}</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setActiveProject('all')}
              className={cn(
                'px-2 py-1.5 rounded-lg text-xs font-medium transition-colors',
                activeProject === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              )}
            >
              {t('projects.all')}
            </button>
            <button
              onClick={() => setActiveProject('hot_mobile')}
              className={cn(
                'px-2 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1',
                activeProject === 'hot_mobile'
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              )}
            >
              <Building2 className="w-3 h-3" />
              <span className="hidden xl:inline">Hot</span>
            </button>
            <button
              onClick={() => setActiveProject('adult_store')}
              className={cn(
                'px-2 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1',
                activeProject === 'adult_store'
                  ? 'bg-pink-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              )}
            >
              <Store className="w-3 h-3" />
              <span className="hidden xl:inline">Adult</span>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-600/20 text-primary-400'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0) || 'M'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{user?.name || 'Mohammad'}</p>
                <p className="text-2xs text-slate-500">{user?.role || 'owner'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="btn-icon text-slate-400 hover:text-red-400"
              aria-label={t('settings.logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4">
          <button
            onClick={toggleSidebar}
            className="btn-icon"
            aria-label={t('common.openMenu')}
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-bold text-white">{t('app.title')}</h1>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
